'use client';

import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecaptchaV3 } from '../../hooks/useRecaptchaV3';
import { useToggle } from '../../hooks/useToggle';
import BroadbandSupportModal from '../Failsafe-Modals/Redirect Modals/BroadbandSupportModal';
import EirSupportModal from '../Failsafe-Modals/Redirect Modals/EirSupportModal';
import MobileSupportModal from '../Failsafe-Modals/Redirect Modals/MobileSupportModal';
import { handleAction } from '../shared/cta-actions/actionHandlers';
import SweeprContainer from '../Sweepr-Application/SweeprContainer';
import AccountVerificationModal from '../Verification-Modals/Account-Verification-Modal/AccountVerificationModal';
import LoginVerificationModal from '../Verification-Modals/Login-Verification-Modal/LoginVerificationModal';
import TroubleshootingTool from './TroubleshootingTool';

/**
 * @name TroubleshootingToolContainer
 * @param {object} props - Strapi and url query params data
 * @returns {JSX.Element} - Renders Troubleshooting tool
 */
const TroubleshootingToolContainer = props => {
  const graphql = useApolloClient();
  const router = useRouter();
  const showLoginVerificationModal = useToggle(false);
  const showAccountVerificationModal = useToggle(false);
  const showMobileSupportModal = useToggle(false);
  const showBroadbandSupportModal = useToggle(false);
  const showEirSupportModal = useToggle(false);
  const [loginValues, setLoginValues] = useState({ email: '', password: '' });
  const [accountValues, setAccountValues] = useState({ accountNumber: '', landLineNumber: '' });
  const [sweeprParams, setSweeprParams] = useState({
    OTC: props?.OTC || null,
    Utterance: props?.Utterance,
    Source: props?.Source,
  });
  const [enableSweepr, setEnableSweepr] = useState(false);
  const [disableCTA, setDisableCTA] = useState(true);
  const [enableCaptchaV2, setEnableCaptchaV2] = useState({ login: false, account: false });
  const [captchaV2, setCaptchaV2] = useState({ login: null, account: null });
  const [apiError, setApiError] = useState({
    invalidLogin: false,
    invalidAccount: false,
    errorMessage: null,
  });
  const maxErrorCount = 2;
  const [errorCount, setErrorCount] = useState(0);
  const { executeRecaptcha } = useRecaptchaV3('sweeprLogin');

  /**
   * @function handleActionWrapper
   * @description Handles function triggered on cta click
   * @param {string} action - Action type to perform
   * @param {URL} redirectUrl - URL provided in strapi
   */
  const handleActionWrapper = (action, redirectUrl) => {
    handleAction(
      action,
      redirectUrl,
      router,
      showLoginVerificationModal,
      showAccountVerificationModal,
      graphql,
      executeRecaptcha,
      loginValues?.email,
      loginValues?.password,
      accountValues?.accountNumber,
      accountValues?.landLineNumber,
      captchaV2,
      setEnableCaptchaV2,
      setApiError,
      setDisableCTA,
      showMobileSupportModal,
      showBroadbandSupportModal,
      showEirSupportModal,
      setSweeprParams,
      setErrorCount,
    );
  };

  const enableModal = useCallback(() => {
    if (sweeprParams?.Utterance && !sweeprParams?.OTC) showLoginVerificationModal.enable();
  }, [sweeprParams?.Utterance]);
  useEffect(() => {
    enableModal();
  }, [enableModal]);

  useEffect(() => {
    if (sweeprParams?.OTC) {
      setEnableSweepr(true);
    } else {
      setEnableSweepr(false);
    }
  }, [sweeprParams?.OTC]);

  return (
    <>
      <TroubleshootingTool
        cmsData={props.cmsData}
        handleAction={handleActionWrapper}
        setSweeprParams={setSweeprParams}
        sweeprParams={sweeprParams}
        hasNotification={props.hasNotification}
        showLoginVerificationModal={showLoginVerificationModal}
      />
      <LoginVerificationModal
        showLoginVerificationModal={showLoginVerificationModal}
        handleAction={handleActionWrapper}
        setDisableCTA={setDisableCTA}
        disableCTA={disableCTA}
        values={loginValues}
        apiError={apiError}
        setValues={setLoginValues}
        captchaV2={captchaV2}
        setCaptchaV2={setCaptchaV2}
        enableCaptchaV2={enableCaptchaV2}
        errorCount={errorCount}
        maxErrorCount={maxErrorCount}
      />
      <AccountVerificationModal
        showAccountVerificationModal={showAccountVerificationModal}
        handleAction={handleActionWrapper}
        setDisableCTA={setDisableCTA}
        apiError={apiError}
        setValues={setAccountValues}
        values={accountValues}
        disableCTA={disableCTA}
        captchaV2={captchaV2}
        setCaptchaV2={setCaptchaV2}
        enableCaptchaV2={enableCaptchaV2}
        errorCount={errorCount}
        maxErrorCount={maxErrorCount}
      />
      <MobileSupportModal
        showMobileSupportModal={showMobileSupportModal}
        handleAction={handleActionWrapper}
      />
      <BroadbandSupportModal
        showBroadbandSupportModal={showBroadbandSupportModal}
        handleAction={handleActionWrapper}
      />
      <EirSupportModal
        showEirSupportModal={showEirSupportModal}
        handleAction={handleActionWrapper}
      />
      {enableSweepr && (
        <SweeprContainer
          OTC={sweeprParams?.OTC}
          Utterance={sweeprParams?.Utterance}
          Source={sweeprParams?.Source}
          setErrorCount={setErrorCount}
          errorCount={errorCount}
          showLoginVerificationModal={showLoginVerificationModal}
          showAccountVerificationModal={showAccountVerificationModal}
        />
      )}
    </>
  );
};
TroubleshootingToolContainer.displayName = 'TroubleshootingToolContainer';
export default TroubleshootingToolContainer;
